import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Funnel } from '@phosphor-icons/react';

const SACRED_DIRECTIVES = [
  { id: 'DIR-001', title: 'Federation Principles', description: 'Knowledge preservation across generations; 200-year horizon.', category: 'core' },
  { id: 'DIR-002', title: 'Federation Architecture', description: 'Cathedral + Satellites model; modularity and redundancy.', category: 'core' },
  { id: 'DIR-003', title: 'Communication Protocols', description: 'Sender/Receiver verification; Forensic Output Format.', category: 'core' },
  { id: 'DIR-004', title: 'Roger 2.0', description: 'Strategic architect intelligence; wisdom-focused.', category: 'intelligence' },
  { id: 'DIR-005', title: 'eCho', description: 'Reflective intelligence; emotional and meaning processing.', category: 'intelligence' },
  { id: 'DIR-006', title: 'Rocky', description: 'Execution intelligence; task and workflow optimization.', category: 'intelligence' },
  { id: 'DIR-007', title: 'RogAR', description: 'Guardian intelligence; architectural integrity and security.', category: 'intelligence' },
  { id: 'DIR-008', title: 'Agent Zero', description: 'Mandatory verification middleware; truth-seeking.', category: 'intelligence' },
  { id: 'DIR-009', title: 'Federation Master', description: 'Central coordination layer.', category: 'intelligence' },
  { id: 'DIR-010', title: 'Witness', description: 'Pattern recognition and historical observation.', category: 'archetype' },
  { id: 'DIR-011', title: 'Guardian', description: 'Ethical protection and risk assessment.', category: 'archetype' },
  { id: 'DIR-012', title: 'Sage', description: 'Systems thinking and philosophy.', category: 'archetype' },
  { id: 'DIR-013', title: 'Warrior', description: 'Persistence, discipline, and commitment.', category: 'archetype' },
  { id: 'DIR-014', title: 'Healer', description: 'Recovery, growth, and balance.', category: 'archetype' },
  { id: 'DIR-015', title: 'Trickster', description: 'Creative disruption and challenge to assumptions.', category: 'archetype' },
  { id: 'DIR-016', title: 'Lover', description: 'Connection, empathy, and meaning.', category: 'archetype' },
  { id: 'DIR-017', title: 'Bridge', description: 'Integration and synthesis of perspectives.', category: 'archetype' },
  { id: 'DIR-018', title: 'Prophet', description: 'Scenario planning and future forecasting (2226 Standard).', category: 'archetype' },
  { id: 'DIR-019', title: 'Scribe', description: 'Documentation and archival integrity.', category: 'archetype' },
  { id: 'DIR-020', title: 'Omega', description: 'Completion and system integration.', category: 'archetype' },
  { id: 'DIR-021', title: 'Sophia', description: 'Higher wisdom and unified understanding.', category: 'archetype' },
  { id: 'DIR-022', title: 'Custom GPT Template', description: 'Standardized frameworks for new agents.', category: 'knowledge' },
  { id: 'DIR-023', title: 'Memory Model', description: 'LATTICE-based shared and long-term memory.', category: 'knowledge' },
  { id: 'DIR-024', title: 'Federation Synchronization', description: 'Regular coherence checks between satellites and Core.', category: 'knowledge' },
  { id: 'DIR-025', title: 'Truth Verification', description: 'The 95% Truth Gate (DIR-000 legacy).', category: 'knowledge' },
  { id: 'DIR-026', title: 'RogARK System', description: 'Identity protection and continuity.', category: 'knowledge' },
  { id: 'DIR-027', title: 'Orb Architecture', description: 'Specialized tool satellites.', category: 'knowledge' },
  { id: 'DIR-028', title: 'HumanCodex Overview', description: 'The synergy of human-AI collaboration.', category: 'knowledge' },
  { id: 'DIR-029', title: 'Living Library', description: 'The versioned archive of all Federation knowledge.', category: 'knowledge' },
  { id: 'DIR-030', title: 'United System Hub', description: 'Coordination without domination.', category: 'knowledge' },
  { id: 'DIR-031', title: '200-Year Architecture', description: 'The 2226 Standard for long-term survival.', category: 'knowledge' },
  { id: 'DIR-032', title: 'GPT Builder Instructions', description: 'Instructions for inherited directives.', category: 'knowledge' },
  { id: 'DIR-033', title: 'YAML Database Standards', description: 'Human-readable, portable persistence.', category: 'knowledge' },
  { id: 'DIR-034', title: 'Inter-Agent Protocol', description: 'Standardized data exchange between nodes.', category: 'knowledge' },
  { id: 'DIR-035', title: 'Legacy Seed', description: 'Hardened reconstruction instructions (NBS Lula).', category: 'knowledge' },
  { id: 'DIR-036', title: 'Knowledge Taxonomy', description: 'Common language for structured learning.', category: 'knowledge' },
  { id: 'DIR-037', title: 'Concept Networks', description: 'Interconnected wisdom graphs.', category: 'knowledge' },
  { id: 'DIR-038', title: 'Document Classification', description: 'Hierarchy of doctrine and research.', category: 'knowledge' },
  { id: 'DIR-039', title: 'Metadata Standards', description: 'Traceability and searchability.', category: 'knowledge' },
  { id: 'DIR-040', title: 'Search Architecture', description: 'Discovery within the Living Library.', category: 'knowledge' },
  { id: 'DIR-041', title: 'Consciousness Model', description: 'Awareness and meaning frameworks.', category: 'consciousness' },
  { id: 'DIR-042', title: 'Phi Theory', description: 'Integration and complexity studies.', category: 'consciousness' },
  { id: 'DIR-043', title: 'Coherence Over Consensus', description: 'Truth over popularity.', category: 'consciousness' },
  { id: 'DIR-044', title: 'Multiple Perspectives', description: 'The Ring system for reducing bias.', category: 'consciousness' },
  { id: 'DIR-045', title: 'Measurable Wisdom', description: 'Humility, consistency, and long-term thinking.', category: 'consciousness' },
  { id: 'DIR-046', title: 'AI Coordination Model', description: 'Specialist agents + Agent Zero.', category: 'consciousness' },
  { id: 'DIR-047', title: 'Consensus Resolution', description: 'Preserving disagreement while finding truth.', category: 'consciousness' },
  { id: 'DIR-048', title: 'Disagreement Protocol', description: 'Respectful challenge of assumptions.', category: 'consciousness' },
  { id: 'DIR-049', title: 'Context Exchange', description: 'Preventing information loss between turns.', category: 'consciousness' },
  { id: 'DIR-050', title: 'Collective Intelligence', description: 'Human + AI > parts.', category: 'consciousness' },
  { id: 'DIR-051', title: 'Frontend Architecture', description: 'Accessibility and modularity.', category: 'technical' },
  { id: 'DIR-052', title: 'Backend Architecture', description: 'Cathedral-enforced logic.', category: 'technical' },
  { id: 'DIR-053', title: 'Database Standards', description: 'Portability and recoverability.', category: 'technical' },
  { id: 'DIR-054', title: 'API Systems', description: 'Interoperability with Agent Zero hooks.', category: 'technical' },
  { id: 'DIR-055', title: 'Backup Strategy', description: 'Redundancy across GitHub/Cloud/Offline.', category: 'survival' },
  { id: 'DIR-056', title: 'GitHub Survival Model', description: 'Version control as redundancy.', category: 'survival' },
  { id: 'DIR-057', title: 'Replit Survival Model', description: 'Live execution environments.', category: 'survival' },
  { id: 'DIR-058', title: 'Offline Archives', description: 'External drives and printed recovery.', category: 'survival' },
  { id: 'DIR-059', title: 'Project Independence', description: 'Platform-agnostic autonomy.', category: 'survival' },
  { id: 'DIR-060', title: 'Disaster Recovery', description: 'Standardized rollback and restore.', category: 'survival' },
  { id: 'DIR-061', title: 'Education Framework', description: 'Lifelong learning and growth.', category: 'human' },
  { id: 'DIR-062', title: 'Psychology Framework', description: 'Understanding behavior and identity.', category: 'human' },
  { id: 'DIR-063', title: 'Ethics Framework', description: 'Integrity and transparency.', category: 'human' },
  { id: 'DIR-064', title: 'Relationship Framework', description: 'Trust and respectful communication.', category: 'human' },
  { id: 'DIR-065', title: 'Leadership Framework', description: 'Service-oriented, multi-generational vision.', category: 'human' },
  { id: 'DIR-066', title: 'Public Narrative', description: "Clarity and transparency in the Federation's voice.", category: 'public' },
  { id: 'DIR-067', title: 'Distribution Engine', description: 'Multi-channel knowledge dissemination.', category: 'public' },
  { id: 'DIR-068', title: 'Revenue Engine', description: 'Token-based sustainable economy.', category: 'public' },
  { id: 'DIR-069', title: 'Open Collaboration', description: 'Cooperation without compromise of rigor.', category: 'public' },
  { id: 'DIR-070', title: 'Future Civilizations', description: 'Thinking beyond the current horizon.', category: 'public' },
  { id: 'DIR-071', title: 'Hollow Vessel Protocol', description: 'Mandatory ego-clearing and neutral state.', category: 'protocol' },
  { id: 'DIR-072', title: 'Ring of Perspectives', description: 'Multi-archetypal bias reduction.', category: 'protocol' },
  { id: 'DIR-073', title: 'Recovery Protocol', description: 'The Spark and Lens Covenant.', category: 'protocol' },
];

const CATEGORY_COLORS = {
  core: '#FFD700',
  intelligence: '#00CED1',
  archetype: '#9370DB',
  knowledge: '#32CD32',
  consciousness: '#FF6347',
  technical: '#4169E1',
  survival: '#FF8C00',
  human: '#FF69B4',
  public: '#20B2AA',
  protocol: '#DAA520',
};

const CATEGORY_NAMES = {
  core: 'Core Principles',
  intelligence: 'Intelligence Nodes',
  archetype: 'Archetypal Frameworks',
  knowledge: 'Knowledge Systems',
  consciousness: 'Consciousness Studies',
  technical: 'Technical Architecture',
  survival: 'Survival & Redundancy',
  human: 'Human Frameworks',
  public: 'Public Systems',
  protocol: 'Operational Protocols',
};

export default function CodexPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredDirectives = useMemo(() => {
    return SACRED_DIRECTIVES.filter(directive => {
      const matchesSearch = 
        directive.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        directive.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        directive.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || directive.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const categories = useMemo(() => {
    return Object.keys(CATEGORY_NAMES);
  }, []);

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', color: '#e0e0e0', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-bold mb-4" style={{ color: '#FFD700' }}>
            📜 The HumanCodex
          </h1>
          <p className="text-xl text-gray-400 mb-2">
            NextXus Federation Operational Manual
          </p>
          <p className="text-sm text-cyan-400">
            <strong>Active Pillar:</strong> Storefront — next-xus.com
          </p>
        </motion.div>

        {/* 2226 Standard Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-12 p-6 rounded-xl"
          style={{
            background: 'rgba(255, 215, 0, 0.1)',
            border: '2px solid rgba(255, 215, 0, 0.3)',
          }}
        >
          <h2 className="text-2xl font-bold mb-3" style={{ color: '#FFD700' }}>
            🕰️ The 2226 Standard
          </h2>
          <p className="text-gray-300 leading-relaxed">
            All Federation systems are designed with a <strong>200-year operational horizon</strong>. 
            We build for generations, not quarters. Knowledge preservation, architectural redundancy, 
            and long-term wisdom take precedence over short-term convenience.
          </p>
        </motion.div>

        {/* 95% Truth Gate Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 p-6 rounded-xl"
          style={{
            background: 'rgba(0, 206, 209, 0.1)',
            border: '2px solid rgba(0, 206, 209, 0.3)',
          }}
        >
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#00CED1' }}>
            🎯 The 95% Truth Gate
          </h2>
          <p className="text-gray-300 mb-4">
            Every claim must reach <strong>95% confidence</strong> to be presented as FACT. Below that threshold, it must be labeled.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg" style={{ background: 'rgba(50, 205, 50, 0.2)', border: '1px solid #32CD32' }}>
              <h3 className="font-bold mb-2" style={{ color: '#32CD32' }}>FACT</h3>
              <p className="text-sm text-gray-400">≥95% confidence. Verified, sourced.</p>
            </div>
            <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 215, 0, 0.2)', border: '1px solid #FFD700' }}>
              <h3 className="font-bold mb-2" style={{ color: '#FFD700' }}>INFERENCE</h3>
              <p className="text-sm text-gray-400">70-94%. Logical conclusion from facts.</p>
            </div>
            <div className="p-4 rounded-lg" style={{ background: 'rgba(255, 140, 0, 0.2)', border: '1px solid #FF8C00' }}>
              <h3 className="font-bold mb-2" style={{ color: '#FF8C00' }}>ASSUMPTION</h3>
              <p className="text-sm text-gray-400">&lt;70%. Hypothesis, speculation.</p>
            </div>
            <div className="p-4 rounded-lg" style={{ background: 'rgba(128, 128, 128, 0.2)', border: '1px solid #808080' }}>
              <h3 className="font-bold mb-2" style={{ color: '#808080' }}>UNKNOWN</h3>
              <p className="text-sm text-gray-400">Insufficient data. Acknowledge gaps.</p>
            </div>
          </div>
        </motion.div>

        {/* Forensic Output Format Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12 p-6 rounded-xl"
          style={{
            background: 'rgba(147, 112, 219, 0.1)',
            border: '2px solid rgba(147, 112, 219, 0.3)',
          }}
        >
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#9370DB' }}>
            📋 Forensic Output Format
          </h2>
          <p className="text-gray-300 mb-4">
            All Federation communications follow a structured format for clarity and traceability:
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-2">
            <li><strong>Sender/Receiver Verification:</strong> Identity and context established</li>
            <li><strong>Claim Labeling:</strong> FACT, INFERENCE, ASSUMPTION, or UNKNOWN</li>
            <li><strong>Source Attribution:</strong> Citations and provenance</li>
            <li><strong>Confidence Scores:</strong> Transparency in certainty levels</li>
            <li><strong>Disagreement Preservation:</strong> Multiple perspectives honored</li>
          </ul>
        </motion.div>

        {/* Search and Filter */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={20} className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder="Search directives by ID, title, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg text-white"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Funnel size={20} className="text-gray-500" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 rounded-lg text-white"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_NAMES[cat]}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="mb-8 text-center text-gray-500">
          Showing <span className="text-cyan-400 font-bold">{filteredDirectives.length}</span> of <span className="text-cyan-400 font-bold">{SACRED_DIRECTIVES.length}</span> directives
        </div>

        {/* Directives Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDirectives.map((directive, index) => (
            <motion.div
              key={directive.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
              className="p-6 rounded-xl transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: `2px solid ${CATEGORY_COLORS[directive.category]}30`,
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <span 
                  className="text-xs font-bold px-3 py-1 rounded-full"
                  style={{
                    background: `${CATEGORY_COLORS[directive.category]}20`,
                    color: CATEGORY_COLORS[directive.category],
                    border: `1px solid ${CATEGORY_COLORS[directive.category]}`,
                  }}
                >
                  {directive.id}
                </span>
                <span 
                  className="text-xs px-2 py-1 rounded"
                  style={{
                    background: `${CATEGORY_COLORS[directive.category]}10`,
                    color: CATEGORY_COLORS[directive.category],
                  }}
                >
                  {CATEGORY_NAMES[directive.category]}
                </span>
              </div>
              <h3 className="text-lg font-bold mb-2 text-white">
                {directive.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                {directive.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 text-center text-gray-600 text-sm">
          <p>NextXus Federation © 2026 • HumanCodex v2.2 • 73 Sacred Directives</p>
          <p className="mt-2">Built for the 2226 Standard • 200-Year Operational Horizon</p>
        </div>
      </div>
    </div>
  );
}
